"use client";

import { IconPause } from "central-icons/IconPause";
import { IconPlay } from "central-icons/IconPlay";
import { type ComponentProps, useState } from "react";
import DemoFooter from "@/components/preview/demo-footer";
import DemoSplit from "@/components/preview/demo-split";
import DemoWrapper from "@/components/preview/demo-wrapper";
import ResultPanel from "@/components/preview/result-panel";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { Button } from "@/components/ui/button";
import { useOscillatingCount } from "@/hooks/use-oscillating-count";
import { cn } from "@/lib/utils";

type TabularNumbersDemoProps = Pick<
  ComponentProps<typeof DemoWrapper>,
  "captionText" | "height" | "margin"
> & { badge?: boolean };

const SAMPLES = [
  { bad: true, className: "", label: "proportional-nums" },
  { bad: false, className: "tabular-nums", label: "tabular-nums" },
];

export default function TabularNumbersDemo({
  badge,
  ...props
}: TabularNumbersDemoProps) {
  const [running, setRunning] = useState(false);
  const count = useOscillatingCount(1000, 1099, running);

  return (
    <DemoWrapper className="shrink-0" {...props}>
      <DemoSplit className="min-h-0 flex-1" stackBelow="sm">
        {SAMPLES.map((sample) => (
          <ResultPanel
            bad={sample.bad}
            innerClassName="py-6 sm:py-16"
            key={sample.label}
            label={badge ? undefined : sample.label}
          >
            <div
              className={cn(
                "select-none rounded-full bg-preview-bg px-4 py-1.5 font-inter font-medium text-2xl shadow-custom",
                sample.className
              )}
            >
              {count}
            </div>
          </ResultPanel>
        ))}
      </DemoSplit>
      <DemoFooter>
        <Button
          aria-label={running ? "Pause" : "Play"}
          onClick={() => setRunning((r) => !r)}
          size="sm"
          variant="secondary"
        >
          <AnimatedIcon
            active={running}
            activeIcon={
              <IconPause className="size-5 text-gray-1200 will-change-transform" />
            }
            idleIcon={
              <IconPlay className="size-5 text-gray-1200 will-change-transform" />
            }
          />
        </Button>
      </DemoFooter>
    </DemoWrapper>
  );
}
